import React from 'react';
import { AlertCircle } from 'lucide-react';

/**
 * Inline per-field error message used by every form in the app.
 *
 * Usage:
 *   <FormError msg={errors.email} testid="reg-email-error" />
 *
 * Renders nothing when `msg` is falsy so callers don't need to wrap it
 * in conditionals.
 */
export function FormError({ msg, testid, className = '' }) {
  if (!msg) return null;
  return (
    <p
      data-testid={testid}
      role="alert"
      className={`text-xs text-rose-600 mt-1.5 flex items-start gap-1 ${className}`}
    >
      <AlertCircle className="w-3.5 h-3.5 mt-px shrink-0" />
      <span>{msg}</span>
    </p>
  );
}

/**
 * Banner shown at the top of a form when there is a global error
 * (e.g. server returned 401, network failed, or multiple field errors).
 *
 * Pre-wraps multi-line messages so bulleted lists render nicely.
 */
export function FormBanner({ msg, testid = 'form-error-banner' }) {
  if (!msg) return null;
  return (
    <div
      data-testid={testid}
      role="alert"
      className="bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-3 rounded-xl flex items-start gap-2"
    >
      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
      <span className="whitespace-pre-line flex-1">{msg}</span>
    </div>
  );
}

export default FormError;
